import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';
import { useSellerSignupData } from '@/hooks/useSellerSignupData';
import { createDefaultOperatingHours, DayOperatingHours } from '@/components/common/WeeklyOperatingHours';
import { Toast } from '@/components/common/Toast';

// Desktop components following SRP
import { SellerSignupHeader } from '@/components/desktop/auth/seller/SellerSignupHeader';
import { StoreInfoForm } from '@/components/desktop/auth/seller/StoreInfoForm';
import { StoreOperatingHours } from '@/components/desktop/auth/seller/StoreOperatingHours';

interface StoreInfo {
  storeName: string;
  storePhone: string;
  address: string;
  detailAddress: string;
  foodTypes: string[];
  customFoodType: string;
  representativeMenus: string[];
}

export function DesktopSellerStoreInfoPage() {
  const navigate = useNavigate();
  const { storeInfo, updateStoreInfo } = useSellerSignupData();

  const [formData, setFormData] = useState<StoreInfo>({
    storeName: storeInfo.storeName || '',
    storePhone: storeInfo.storePhone || '',
    address: storeInfo.address || '',
    detailAddress: storeInfo.detailAddress || '',
    foodTypes: storeInfo.foodTypes || [] as string[],
    customFoodType: storeInfo.customFoodType || '',
    representativeMenus: storeInfo.representativeMenus || ['', '', '']
  });

  const [operatingHours, setOperatingHours] = useState<DayOperatingHours[]>(
    storeInfo.operatingHours && storeInfo.operatingHours.length > 0 
      ? storeInfo.operatingHours 
      : createDefaultOperatingHours()
  );
  
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });

  const handleFormChange = (field: keyof StoreInfo, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // localStorage에 자동 저장
    updateStoreInfo({ [field]: value });
  };

  const handleMenuChange = (index: number, value: string) => {
    const newMenus = formData.representativeMenus.map((menu, i) => 
      i === index ? value : menu
    );
    setFormData(prev => ({
      ...prev,
      representativeMenus: newMenus
    }));
    
    // localStorage에 자동 저장
    updateStoreInfo({ representativeMenus: newMenus });
  };

  const handleFoodTypeChange = (type: string) => {
    setFormData(prev => {
      let newData;
      
      if (type === '기타') {
        // 기타 선택 시
        newData = {
          ...prev,
          foodTypes: ['기타'],
          customFoodType: prev.customFoodType
        };
      } else {
        // 다른 타입 선택 시 (항상 1개만 선택)
        newData = {
          ...prev,
          foodTypes: [type],
          customFoodType: ''  // 기타가 아니면 customFoodType 초기화
        };
      }
      
      // localStorage에 자동 저장
      updateStoreInfo({ 
        foodTypes: newData.foodTypes,
        customFoodType: newData.customFoodType
      });
      
      return newData;
    });
  };

  const handleAddressSelected = (addressData: {
    roadAddress: string;
    jibunAddress: string;
    zonecode: string;
  }) => {
    const newAddress = addressData.roadAddress || addressData.jibunAddress;
    setFormData(prev => ({
      ...prev,
      address: newAddress
    }));
    
    // localStorage에 자동 저장
    updateStoreInfo({ address: newAddress });
    
    // 주소 선택 후 모달 자동 닫기
    setShowAddressModal(false);
  };

  const handleOperatingHoursChange = (hours: DayOperatingHours[]) => {
    setOperatingHours(hours);
    // localStorage에 자동 저장
    updateStoreInfo({ operatingHours: hours });
  };

  const isFormValid = () => {
    const hasFoodTypes = formData.foodTypes.length > 0 && 
      (formData.foodTypes.includes('기타') ? formData.customFoodType.trim() !== '' : true);
    
    const hasValidOperatingHours = operatingHours.some(day => 
      !day.isClosed && day.openTime && day.closeTime
    );
    
    return formData.storeName &&
           formData.storePhone &&
           formData.address &&
           hasFoodTypes &&
           hasValidOperatingHours &&
           formData.representativeMenus.some(menu => menu.trim() !== '');
  };

  const handleComplete = () => {
    
    if (!isFormValid()) {
      setToast({ show: true, message: '모든 필수 항목을 입력해주세요' });
      return;
    }

    // 최종 단계로 이동 - 회원가입 완료
    navigate('/signup/seller/complete');
  };

  return (
    <DesktopLayout>
      <div className="min-h-[80vh] flex items-center justify-center py-12">
        <div className="w-full max-w-7xl bg-white rounded-2xl shadow-lg border border-gray-200 px-12 py-16">
          
          {/* Header Section */}
          <SellerSignupHeader 
            title="매장 정보 입력"
            subtitle="매장 운영에 필요한 정보를 입력해주세요"
            currentStep={3}
            totalSteps={3}
            className="mb-12" 
          />
          
          {/* Form Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* 좌측: 매장 정보 폼 */}
            <div>
              <StoreInfoForm
                formData={formData}
                onFormChange={handleFormChange}
                onMenuChange={handleMenuChange}
                onFoodTypeChange={handleFoodTypeChange}
                onAddressSelected={handleAddressSelected}
                showAddressModal={showAddressModal}
                onShowAddressModal={setShowAddressModal}
              />
            </div>
            
            {/* 우측: 영업시간 및 완료 버튼 */}
            <div className="lg:col-span-1">
              <div className="sticky top-8">
                <StoreOperatingHours
                  operatingHours={operatingHours}
                  onOperatingHoursChange={handleOperatingHoursChange}
                />
                
                {/* 완료 버튼 */}
                <div className="mt-8">
                  <button
                    onClick={handleComplete}
                    disabled={!isFormValid()}
                    className={`w-full h-[56px] font-semibold text-[20px] rounded-xl transition-colors ${
                      isFormValid() 
                        ? 'bg-[#51C854] text-white hover:bg-[#46B649] active:scale-[0.98]' 
                        : 'bg-[#DEDEDE] text-[#999999] cursor-not-allowed'
                    }`}
                  >
                    회원가입 완료
                  </button>
                </div>

                {/* 도움말 */}
                <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="text-[16px] font-medium text-green-700 mb-2">
                    🎉 마지막 단계입니다!
                  </h4>
                  <ul className="space-y-1 text-[14px] text-green-600">
                    <li>• 모든 정보가 정확한지 한 번 더 확인해주세요</li>
                    <li>• 완료 버튼 클릭 시 심사가 진행됩니다</li>
                    <li>• 심사는 1-2 영업일 소요됩니다</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 토스트 메시지 */}
      {toast.show && (
        <Toast
          message={toast.message}
          isVisible={toast.show}
          onClose={() => setToast({ show: false, message: '' })}
        />
      )}
    </DesktopLayout>
  );
}